import { useState, useEffect } from 'react'
import { ResponsiveBar } from '@nivo/bar'
import s from './OpportunitiesByInternalTeamRole.scss'
import { makeReportsPostRequest, makeGetRequest, getReportsBaseUrl } from 'utils/api'
import { toBarChartData, toBarChartDataByWorkType, toTable, toUsers, toRoles } from './serializers'
import { toTeams } from 'utils/serializers'
import { useLoading } from 'hooks'
import { Col, Row } from 'reactstrap'
import { getCurrency, getToken } from 'utils/authService'
import MaterialTable from '@material-table/core'
import { NumericFormat } from 'react-number-format'
import { RangeDatePicker } from 'components/ideal-core-ui'
import { utcDate, currencyFormatter } from 'utils/helpers'
import isEmpty from 'lodash/isEmpty'
import cn from 'classnames'
import Select from 'react-select'
import { FaFileExcel } from 'react-icons/fa'
import Cookies from 'universal-cookie'
import { COOKIES } from 'utils/constants'
import moment from 'moment'
import { AddButton } from 'components/common/AddButton'
import { ExportCsv, ExportPdf } from '@material-table/exporters'

const groupByOptions = [
  { value: 'summary', label: 'Summary' },
  { value: 'workType', label: 'By Work Type' },
  { value: 'team', label: 'By Team' },
  { value: 'teamWorkType', label: 'By Team + Work Type' }
]

const cookies = new Cookies()

const buildAllRow = info => ({
  groupLabel: 'All',
  estimator: 'All',
  team: 'All',
  workType: '-',
  bidCount: info?.totalBidCount || 0,
  wonCount: info?.totalWonCount || 0,
  bidValue: info?.totalBidValue || 0,
  wonValue: info?.totalWonValue || 0,
  winCountPercentage:
    info?.totalBidCount > 0
      ? Number(((info.totalWonCount / info.totalBidCount) * 100).toFixed(2))
      : 0,
  winValuePercentage:
    info?.totalBidValue > 0
      ? Number(((info.totalWonValue / info.totalBidValue) * 100).toFixed(2))
      : 0,
  workTypeWonShare: 100
})

const valueTooltipContent = (value, isCurrency) =>
  isCurrency ? (
    <NumericFormat
      thousandSeparator
      decimalScale={2}
      value={value}
      prefix={getCurrency().symbol}
      displayType="text"
    />
  ) : (
    value
  )

const TOOLTIP_LABELS = {
  bidValue: 'Bid Value',
  wonValue: 'Won Value',
  bidCount: 'Bid Count',
  wonCount: 'Won Count'
}

const totalChartTooltip = ({ id, value }, type) => {
  const label = TOOLTIP_LABELS[id] || id
  return (
    <span>
      {label}: <strong>{valueTooltipContent(value, type === 'value')}</strong>
    </span>
  )
}

const barChartTooltip = ({ id, value }, type, groupByWorkType) => {
  return (
    <span>
      {id}: <strong>{valueTooltipContent(value, type === 'value' || groupByWorkType)}</strong>
    </span>
  )
}

const OpportunitiesByInternalTeamRole = () => {
  const [pipelineInfo, setPipelineInfo] = useState(null)
  const [tableInfo, setTableInfo] = useState([])
  const [result, setResult] = useState([])
  const [users, setUsers] = useState([])
  const [userFilter, setUserFilter] = useState([])
  const [teams, setTeams] = useState([])
  const [teamFilter, setTeamFilter] = useState([])
  const [, withLoadingLocks] = useLoading()
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [type, setType] = useState('value')
  const [groupBy, setGroupBy] = useState('summary')
  const [roles, setRoles] = useState([])
  const [role, setRole] = useState('ESTIMATOR')

  const groupByWorkType = groupBy === 'workType' || groupBy === 'teamWorkType'
  const groupByTeam = groupBy === 'team' || groupBy === 'teamWorkType'
  const primaryLabel = groupByTeam ? 'Team' : 'Estimator'

  const setDateFilters = (dates, utc = true) => {
    setStartDate(utc ? utcDate(dates.from) : dates.from)
    setEndDate(utc ? utcDate(dates.to) : dates.to)
  }

  useEffect(() => {
    const sessionDateRangeFilters = cookies.get(COOKIES.DATE_RANGE_FILTERS)
    if (sessionDateRangeFilters?.startDate) {
      setDateFilters(
        {
          from: sessionDateRangeFilters.startDate,
          to: sessionDateRangeFilters.endDate
        },
        false
      )
    } else {
      setDateFilters({
        from: moment().subtract(2, 'weeks').startOf('day').format(),
        to: moment().add(6, 'weeks').endOf('day').format()
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    ;(async () => {
      const { content } = await makeGetRequest('/v1/users')
      setUsers(toUsers(content.filter(user => user.active !== false)))
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      const response = await makeGetRequest('/v1/teams')
      setTeams(toTeams(response))
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      const response = await makeGetRequest('/v1/internalteamroles')
      setRoles(toRoles(response))
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      if (!startDate || !endDate) return
      const userFilterParam =
        userFilter?.length > 0 ? { userFilter: userFilter.map(user => user.value) } : {}
      const teamFilterParam =
        teamFilter?.length > 0 ? { teamFilter: teamFilter.map(team => team.value) } : {}
      const response = await withLoadingLocks(
        makeReportsPostRequest('/v1/reports/estimator', {
          from: startDate,
          to: endDate,
          ...userFilterParam,
          ...teamFilterParam,
          groupByTeam,
          groupByWorkType,
          role
        })
      )
      setResult(response)
      if (groupByWorkType) {
        setPipelineInfo(toBarChartDataByWorkType(response.estimatorPerformance))
      } else {
        setPipelineInfo(toBarChartData(response.estimatorPerformance))
      }
      setTableInfo(toTable(response.estimatorPerformance))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, userFilter, teamFilter, groupByTeam, groupByWorkType, role])

  const onBtnExport = () => {
    window.open(
      getReportsBaseUrl(
        `/v1/reports/estimator/export?from=${startDate}&to=${endDate}&userFilter=${userFilter
          .map(user => user.value)
          .join()}&teamFilter=${teamFilter
          .map(team => team.value)
          .join()}&groupByTeam=${groupByTeam}&groupByWorkType=${groupByWorkType}&role=${role}&token=${getToken()}`
      ),
      '_blank'
    )
  }

  const columns = [
    { title: primaryLabel, field: 'groupLabel', sorting: true },
    ...(groupByWorkType ? [{ title: 'Work Type', field: 'workType', sorting: true }] : []),
    { title: 'Bid Count', field: 'bidCount', sorting: true },
    { title: 'Won Count', field: 'wonCount', sorting: true },
    {
      title: 'Bid Value',
      field: 'bidValue',
      sorting: true,
      render: row => (
        <NumericFormat
          thousandSeparator
          decimalScale={2}
          value={row.bidValue}
          prefix={getCurrency().symbol}
          displayType="text"
        />
      ),
      exportTransformer: row => currencyFormatter({ value: row.bidValue })
    },
    {
      title: 'Won Value',
      field: 'wonValue',
      sorting: true,
      render: row => (
        <NumericFormat
          thousandSeparator
          decimalScale={2}
          value={row.wonValue}
          prefix={getCurrency().symbol}
          displayType="text"
        />
      ),
      exportTransformer: row => currencyFormatter({ value: row.wonValue })
    },
    {
      title: 'Win Rate by Count',
      field: 'winCountPercentage',
      sorting: true,
      render: row => `${Number(row.winCountPercentage).toFixed(2)}%`,
      exportTransformer: row => `${Number(row.winCountPercentage).toFixed(2)}%`
    },
    {
      title: 'Win Rate by Value',
      field: 'winValuePercentage',
      sorting: true,
      render: row => `${Number(row.winValuePercentage).toFixed(2)}%`,
      exportTransformer: row => `${Number(row.winValuePercentage).toFixed(2)}%`
    },
    ...(groupByWorkType
      ? [
          {
            title: 'Work Type Won Share',
            field: 'workTypeWonShare',
            sorting: true,
            render: row => `${Number(row.workTypeWonShare).toFixed(2)}%`,
            exportTransformer: row => `${Number(row.workTypeWonShare).toFixed(2)}%`
          }
        ]
      : [])
  ]

  const chartKeys = type === 'value' ? ['bidValue', 'wonValue'] : ['bidCount', 'wonCount']

  const chartData = pipelineInfo?.barByEstimator || []

  return (
    <>
      <h6 className={s.title}>Sales Reports &gt; Opportunities by Internal Team Role</h6>
      <div className={s.filters}>
        <button
          type="button"
          onClick={() => setType('count')}
          className={cn(s.toggler, {
            [s.active]: type === 'count'
          })}
        >
          Count
        </button>
        <button
          type="button"
          onClick={() => setType('value')}
          className={cn(s.toggler, {
            [s.active]: type === 'value'
          })}
        >
          Value
        </button>
        <RangeDatePicker startDate={startDate} endDate={endDate} onChange={setDateFilters} />
        <br />
        <Select
          onChange={option => setRole(option.value)}
          options={roles}
          value={roles.find(o => o.value === role)}
          placeholder="Select Internal Team Role"
          className={s.groupBySelect}
        />
        <Select
          onChange={values => setUserFilter(values)}
          options={users}
          value={userFilter}
          placeholder="Select User(s)"
          className={s.userSelect}
          isMulti
        />
        <Select
          onChange={values => setTeamFilter(values)}
          options={teams}
          value={teamFilter}
          placeholder="Select Team(s)"
          className={s.userSelect}
          isMulti
        />
        <Select
          onChange={option => setGroupBy(option.value)}
          options={groupByOptions}
          value={groupByOptions.find(o => o.value === groupBy)}
          placeholder="Group by"
          className={s.groupBySelect}
        />
      </div>
      {result.estimatorPerformance?.length > 0 && !isEmpty(pipelineInfo) ? (
        <>
          <Row>
            <Col lg="1">
              <div className={s.info}>
                <p>Total Bid Value: </p>
                <p>
                  <NumericFormat
                    thousandSeparator
                    decimalScale={2}
                    value={pipelineInfo.totalBidValue || 0}
                    prefix={getCurrency().symbol}
                    displayType="text"
                  />
                </p>
                <p>Total Won Value: </p>
                <p>
                  <NumericFormat
                    thousandSeparator
                    decimalScale={2}
                    value={pipelineInfo.totalWonValue || 0}
                    prefix={getCurrency().symbol}
                    displayType="text"
                  />
                </p>
                <p>Bids: {pipelineInfo.totalBidCount || 0}</p>
                <p>Won: {pipelineInfo.totalWonCount || 0}</p>
              </div>
            </Col>
            <Col lg="11">
              <div className={s.totalChart}>
                <ResponsiveBar
                  data={[
                    {
                      bidValue: pipelineInfo.totalBidValue || 0,
                      wonValue: pipelineInfo.totalWonValue || 0
                    }
                  ]}
                  keys={chartKeys}
                  margin={{ top: 0, right: 130, bottom: 50, left: 60 }}
                  padding={0.15}
                  layout="horizontal"
                  colors={{ scheme: 'nivo' }}
                  borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={null}
                  axisLeft={null}
                  tooltip={d => totalChartTooltip(d, type)}
                  enableGridY={false}
                  enableLabel={false}
                  labelSkipWidth={12}
                  labelSkipHeight={9}
                  labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                  animate
                  motionStiffness={90}
                  motionDamping={15}
                />
              </div>
            </Col>
          </Row>
          <div
            className={s.barChart}
            style={{
              height: (chartData.length || 1) * 60 + 'px',
              maxHeight: 800
            }}
          >
            <ResponsiveBar
              data={chartData}
              keys={groupByWorkType ? pipelineInfo.keys : chartKeys}
              indexBy="groupLabel"
              margin={{ top: 0, right: 200, bottom: 50, left: 170 }}
              padding={0.45}
              layout="horizontal"
              colors={{ scheme: 'nivo' }}
              borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legendPosition: 'middle',
                legendOffset: 32
              }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 5,
                tickRotation: 0,
                legendPosition: 'middle',
                legendOffset: -40
              }}
              tooltip={d => barChartTooltip(d, type, groupByWorkType)}
              enableGridY
              enableGridX
              enableLabel={false}
              labelSkipWidth={12}
              labelSkipHeight={9}
              labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              legends={[
                {
                  dataFrom: 'keys',
                  anchor: 'bottom-right',
                  direction: 'column',
                  justify: false,
                  translateX: 120,
                  translateY: 0,
                  itemsSpacing: 14,
                  itemWidth: 100,
                  itemHeight: 20,
                  itemDirection: 'left-to-right',
                  itemOpacity: 0.85,
                  symbolSize: 20,
                  toggleSerie: true,
                  effects: [
                    {
                      on: 'hover',
                      style: {
                        itemOpacity: 1
                      }
                    }
                  ]
                }
              ]}
              animate
              motionStiffness={90}
              motionDamping={15}
            />
          </div>
          <div className={s.table}>
            <AddButton className={s.exportExcel} onClick={onBtnExport}>
              <FaFileExcel className={s.excelIcon} />
              Export to Excel
            </AddButton>
            <MaterialTable
              title=""
              columns={columns}
              data={[buildAllRow(pipelineInfo), ...tableInfo]}
              options={{
                actionsColumnIndex: -1,
                sorting: true,
                headerStyle: {
                  backgroundColor: '#eee',
                  color: '#000',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  fontSize: '14px'
                },
                maxBodyHeight: '500px',
                pageSize: 50,
                padding: 'dense',
                pageSizeOptions: [
                  5,
                  10,
                  20,
                  50,
                  {
                    value: [buildAllRow(pipelineInfo), ...tableInfo].length,
                    label: 'All'
                  }
                ],
                emptyRowsWhenPaging: false,
                exportMenu: [
                  {
                    label: 'Export PDF',
                    exportFunc: (cols, datas) =>
                      ExportPdf(cols, datas, 'Opportunities by Internal Team Role')
                  },
                  {
                    label: 'Export CSV',
                    exportFunc: (cols, datas) =>
                      ExportCsv(cols, datas, 'Opportunities by Internal Team Role')
                  }
                ],
                columnsButton: true
              }}
              style={{
                boxShadow: 'none',
                fontSize: '14px'
              }}
            />
          </div>
        </>
      ) : (
        <p className={s.noResults}>No results to display. Try different filters.</p>
      )}
    </>
  )
}

export default OpportunitiesByInternalTeamRole
